import { InputWrapper } from '@vrplatform/components';
import {
  type gqlV2,
  useAccountingConnectionQuery,
  useTrpcMutation,
  useTeam,
} from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { showErrorNotification } from '@vrplatform/theme';
import {
  type Maybe,
  bankersRoundToCents,
  ensure,
  formatCurrency,
  utc,
} from '@vrplatform/utils';
import {
  Card,
  Divider,
  Group,
  NumberInput,
  Text,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import type { owner_statement_status_enum } from '@vrplatform/graphql';
import { getCurrencySymbol } from '@vrplatform/ui-common';
import { Controller, useForm } from 'react-hook-form';
import { useAutomationHistoryDrawer } from '../../../../hooks';
import { PlaceholderBadge } from './PlaceholderBagde';

type Props = {
  mode: 'view' | 'preview';
  statementId: string | undefined;
  // periodId: string | undefined;
  statementStatus: Maybe<owner_statement_status_enum>;
  currency: string | undefined;
  ownerSplit?: number | undefined;
  startAt: Maybe<string>;
  balances: {
    startingBalance: number;
    netIncome: number;
    currentBalance: number;
    ownerPayout: number;
    ownerPayoutSplit?: number;
    endingBalance: number;
  };
};

export const StatementRevenueSummary = ({
  balances,
  mode,
  statementId,
  // periodId,
  currency,
  ownerSplit,
  statementStatus,
  startAt,
}: Props) => {
  const backgroundColor = '#ffffff40';

  return (
    <Card
      p="xl"
      radius="md"
      withBorder
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.md,
        backgroundColor,
        boxShadow: '0 2px 2px 0 #09090B05, 0 0 0 1px #09090B07',
      })}
    >
      {startAt ? (
        <Text component="span" size="lg" m={0} fw={500} mb={rem(5)}>
          {startAt ? utc(startAt).format('MMMM YYYY') : 'Missing Month'} -
          Summary
        </Text>
      ) : (
        <Text component="span" size="lg" m={0} fw={500}>
          <PlaceholderBadge>Month</PlaceholderBadge> - Summary
        </Text>
      )}
      <SummaryLine title="Starting Balance">
        {formatValue(balances.startingBalance, currency)}
      </SummaryLine>
      <SummaryLine title="Net Income" isPrimary>
        {formatValue(balances.netIncome, currency)}
      </SummaryLine>
      <SummaryLine title="Current Balance">
        {formatValue(balances.currentBalance, currency)}
      </SummaryLine>
      {mode === 'view' &&
      statementId &&
      statementStatus &&
      ensure<gqlV2.owner_statement_status_enum[]>([
        'draft',
        'inReview',
      ]).includes(statementStatus) ? (
        <OwnerPayoutInput
          statementId={statementId}
          currency={currency}
          payout={balances.ownerPayout as number}
          status={statementStatus}
        />
      ) : (
        <SummaryLine title="Owner payout">
          {formatValue(balances.ownerPayout, currency)}
        </SummaryLine>
      )}
      {typeof balances.ownerPayoutSplit !== 'undefined' &&
        balances.ownerPayoutSplit !== balances.ownerPayout && (
          <SummaryLine title={`Owner Split (${ownerSplit}%)`}>
            {formatValue(balances.ownerPayoutSplit, currency)}
          </SummaryLine>
        )}
      <Divider />
      <SummaryLine title="Ending Balance" isBold>
        {formatValue(balances.endingBalance, currency)}
      </SummaryLine>
    </Card>
  );
};

const formatValue = (value: string | number, currency: string | undefined) => {
  if (typeof value === 'string') return value;

  return formatCurrency(value, currency || 'usd');
};

const SummaryLine = ({
  isPrimary,
  children,
  title,
  isBold,
}: {
  isPrimary?: boolean;
  isBold?: boolean;
  title: string;
  children: React.ReactNode;
}) => {
  const { primaryColor, colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDarkMode = colorScheme === 'dark';

  return (
    <Group justify="space-between">
      <Text component="p" fw={isBold ? 500 : undefined} m={0}>
        {title}
      </Text>
      <Text
        component="p"
        fw={500}
        c={
          isPrimary
            ? isDarkMode
              ? colors[primaryColor][5]
              : primaryColor
            : undefined
        }
        m={0}
      >
        {children}
      </Text>
    </Group>
  );
};

const OwnerPayoutInput = ({
  currency,
  payout,
  statementId,
  status,
}: {
  statementId: string;
  currency: string | undefined;
  payout: number;
  status: owner_statement_status_enum;
}) => {
  const { accounting } = useAccountingConnectionQuery();
  const [{ id: teamId }] = useTeam();

  const allowNegativePayout = accounting?.appId === 'intacct';

  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();

  const { control, clearErrors, handleSubmit } = useForm<{ payout: number }>({
    mode: 'onChange',
    values: { payout },
  });

  const { mutate, loading } = useTrpcMutation('publishOwnerStatement', {
    invalidateQueryKeys: ['ownerStatements', 'ownerStatementTemplates'],
  });

  const publishStatement = handleSubmit(async (args: { payout: number }) => {
    if (!statementId) return;
    if (bankersRoundToCents(args.payout) === bankersRoundToCents(payout))
      return;

    return await mutate({
      teamId,
      statements: [
        {
          id: statementId,
          centTotalOverwrite: bankersRoundToCents(args.payout),
        },
      ],
      status,
      notification: 'disabled', // notfication disabled when status !== publish/posted
    }).then((x) => {
      const syncId = x.task?.syncId;
      if (syncId) openAutomationHistoryDrawer(syncId);
      return { ok: true, task: x.task };
    });
  });

  return (
    <Group justify="space-between">
      <Text component="p" m={0}>
        Owner Payout
      </Text>
      <Controller
        control={control}
        name="payout"
        rules={{
          min: allowNegativePayout
            ? undefined
            : {
                value: 0,
                message: 'Payout cannot be negative.',
              },
        }}
        render={({
          fieldState: { error },
          field: { value, onChange, name },
        }) => {
          return (
            <InputWrapper error={error?.message}>
              <NumberInput
                name={name}
                onChange={onChange}
                sx={(theme) => ({
                  height: '100%',
                  'input:focus': {
                    boxShadow: `0px 0px 0px 2px ${
                      theme.colors[theme.primaryColor][4]
                    }40`,
                  },
                })}
                value={value ? value : undefined}
                maw={150}
                decimalScale={2}
                hideControls
                leftSection={
                  loading ? (
                    <LoadingIndicator size="xs" />
                  ) : (
                    getCurrencySymbol(currency)
                  )
                }
                onBlur={() => {
                  if (error) {
                    onChange(payout);
                    showErrorNotification({
                      title: 'Invalid validation',
                      message: error.message,
                    });
                    clearErrors();
                    return;
                  }

                  publishStatement();
                }}
                placeholder="0.00"
                // type="number"
                styles={{
                  input: {
                    textAlign: 'right',
                  },
                }}
              />
            </InputWrapper>
          );
        }}
      />
    </Group>
  );
};
